import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatEUR } from "@/lib/constants";

const mmYYYY = (p) => (p && p.length >= 7 ? `${p.slice(5, 7)}/${p.slice(0, 4)}` : p);

// Hinweis oben im Mietkonto: Guthaben UND fällige offene Posten.
export function SurplusOfferBanner({ offer, onOpen }) {
  if (!offer) return null;
  const first = offer.rows?.[0];
  const label = offer.rows.length === 1
    ? `${first.label} ${mmYYYY(first.period)}`
    : `${offer.rows.length} offenen Posten (ab ${mmYYYY(first.period)})`;
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex flex-wrap items-center gap-2"
         data-testid="surplus-offer-banner">
      <span>
        Guthaben <b>{formatEUR(offer.surplus)}</b> kann mit {label} verrechnet werden ({formatEUR(offer.total)}).
      </span>
      <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700" onClick={onOpen} data-testid="surplus-offer-open">
        Guthaben verrechnen
      </Button>
    </div>
  );
}

export function SurplusOffsetDialog({ open, onClose, tenancyId, onDone }) {
  const [offer, setOffer] = useState(null);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOffer(null);
    setNotify(true);
    api.get(`/tenancies/${tenancyId}/surplus-offer`).then(({ data }) => setOffer(data.offer || false))
      .catch(() => setOffer(false));
  }, [open, tenancyId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenancies/${tenancyId}/reallocate`, { notify });
      const n = data.notice;
      toast.success(`${formatEUR(data.amount)} aus Guthaben verrechnet` + (n?.status === "sent"
        ? ` · Mitteilung an Mieter per ${n.channel === "email" ? "E-Mail versendet" : "Brief im Schriftverkehr abgelegt"}`
        : n?.status === "failed" ? " · Mitteilung fehlgeschlagen" : ""));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verrechnung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="surplus-offset-dialog">
        <DialogHeader>
          <DialogTitle>Guthaben verrechnen</DialogTitle>
          <DialogDescription>Fällige offene Posten werden aus dem Guthaben bedient (ältester Monat zuerst, Kaltmiete zuerst). Der Saldo bleibt gleich.</DialogDescription>
        </DialogHeader>
        {offer === null && <div className="text-sm text-slate-500 py-4">Lädt…</div>}
        {offer === false && (
          <div className="text-sm text-slate-600 py-4" data-testid="surplus-offset-nothing">
            Nichts zu verrechnen — kein Guthaben oder keine fälligen offenen Posten.
          </div>
        )}
        {offer && (
          <div className="space-y-3">
            <table className="w-full text-sm" data-testid="surplus-offset-rows">
              <tbody>
                {offer.rows.map((r) => (
                  <tr key={`${r.period}-${r.category}`} className="border-b border-slate-100">
                    <td className="py-1">{r.label}</td>
                    <td className="py-1 text-slate-500">{mmYYYY(r.period)}</td>
                    <td className="py-1 text-right font-mono">{formatEUR(r.amount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="pt-1.5" colSpan={2}>Summe (Guthaben {formatEUR(offer.surplus)})</td>
                  <td className="pt-1.5 text-right font-mono">{formatEUR(offer.total)}</td>
                </tr>
              </tbody>
            </table>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="surplus-offset-notify" />
              Mitteilung an den Mieter senden (E-Mail, sonst Brief im Schriftverkehr)
            </label>
            {notify && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700 max-h-52 overflow-y-auto"
                   data-testid="surplus-offset-notice-preview">
                <div className="font-semibold mb-1">{offer.notice_subject}</div>
                <div dangerouslySetInnerHTML={{ __html: offer.notice_html.replace("{{recipient.greeting}}", "Sehr geehrte/r Mieter/in,") }} />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !offer} className="bg-emerald-700 hover:bg-emerald-800" data-testid="surplus-offset-submit">
            {busy ? "Verrechnet…" : "Jetzt verrechnen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
